import { motion } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import DOMPurify from 'dompurify'

interface AIInvestigationBriefProps {
  aiInsights: any
  loadingInsights: boolean
  breachCount: number
  query: string
  onReanalyze?: () => void
}

const RISK_COLORS: Record<string, { bg: string; border: string; text: string; glow: string }> = {
  CRITICAL: { bg: 'bg-red-600', border: 'border-red-500', text: 'text-red-400', glow: 'shadow-[0_0_20px_rgba(239,68,68,0.5)]' },
  HIGH: { bg: 'bg-orange-600', border: 'border-orange-500', text: 'text-orange-400', glow: 'shadow-[0_0_20px_rgba(249,115,22,0.4)]' },
  MEDIUM: { bg: 'bg-yellow-600', border: 'border-yellow-500', text: 'text-yellow-400', glow: 'shadow-[0_0_15px_rgba(234,179,8,0.3)]' },
  LOW: { bg: 'bg-green-600', border: 'border-green-500', text: 'text-green-400', glow: 'shadow-[0_0_15px_rgba(34,197,94,0.3)]' },
  UNKNOWN: { bg: 'bg-gray-600', border: 'border-gray-500', text: 'text-gray-400', glow: '' },
}

export function AIInvestigationBrief({ aiInsights, loadingInsights, breachCount, query, onReanalyze }: AIInvestigationBriefProps) {

  if (loadingInsights) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-purple-500/30 rounded-lg bg-gradient-to-r from-purple-900/20 via-black to-purple-900/20 p-6"
      >
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full"
          />
          <div>
            <div className="text-purple-400 font-mono text-xs font-semibold tracking-widest">AI ANALYSIS IN PROGRESS</div>
            <div className="text-gray-500 text-[11px] mt-0.5 font-mono">
              Analyzing {breachCount} breach sources, correlating identifiers, generating investigative brief...
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  if (!aiInsights) return null

  const riskLevel = aiInsights?.risk_level || 'UNKNOWN'
  const fullMarkdown = aiInsights?.insights || ''
  const riskColors = RISK_COLORS[riskLevel] || RISK_COLORS.UNKNOWN

  if (!fullMarkdown) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border ${riskColors.border} rounded-lg bg-gradient-to-r from-gray-900/80 via-black to-gray-900/80 overflow-hidden ${riskColors.glow}`}
    >
      {/* Compact header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-800/60 bg-black/50">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="font-mono text-[9px] text-gray-500 tracking-widest uppercase">AI Investigation Brief</span>
          <div className="h-3 w-px bg-gray-700" />
          <Badge className={`${riskColors.bg} text-white text-[10px] font-mono font-bold border ${riskColors.border} px-2 py-0 leading-5`}>
            {riskLevel} RISK
          </Badge>
          {aiInsights.model && aiInsights.model !== 'error' && (
            <span className="font-mono text-[9px] text-gray-600 bg-gray-800/60 border border-gray-700/40 px-1.5 py-0.5 rounded">
              {aiInsights.model}
            </span>
          )}
          <span className="font-mono text-[9px] text-gray-700 truncate max-w-[180px]">{query}</span>
        </div>
        {onReanalyze && (
          <button
            onClick={onReanalyze}
            className="shrink-0 px-2.5 py-1 text-[10px] font-mono rounded bg-purple-600/20 text-purple-400 border border-purple-500/30 hover:bg-purple-600/40 hover:text-purple-300 transition-colors"
          >
            Re-analyze
          </button>
        )}
      </div>

      {/* Full analysis — always rendered, no toggle */}
      <div className="px-5 py-4 prose prose-invert prose-sm max-w-none
        [&_h1]:text-purple-400 [&_h1]:font-mono [&_h1]:text-[11px] [&_h1]:tracking-widest [&_h1]:uppercase [&_h1]:border-b [&_h1]:border-purple-900/40 [&_h1]:pb-1.5 [&_h1]:mb-3 [&_h1]:mt-5 [&_h1:first-child]:mt-0
        [&_h2]:text-purple-400 [&_h2]:font-mono [&_h2]:text-[11px] [&_h2]:tracking-widest [&_h2]:uppercase [&_h2]:border-b [&_h2]:border-purple-900/40 [&_h2]:pb-1.5 [&_h2]:mb-3 [&_h2]:mt-5 [&_h2:first-child]:mt-0
        [&_h3]:text-purple-300 [&_h3]:font-mono [&_h3]:text-[10px] [&_h3]:tracking-widest [&_h3]:uppercase [&_h3]:mt-4 [&_h3]:mb-2
        [&_p]:text-gray-300 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:mb-3
        [&_strong]:text-white [&_strong]:font-semibold
        [&_em]:text-gray-400
        [&_code]:text-green-400 [&_code]:bg-green-950/40 [&_code]:border [&_code]:border-green-900/40 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
        [&_ul]:space-y-1.5 [&_ul]:my-2
        [&_ol]:space-y-1.5 [&_ol]:my-2
        [&_li]:text-gray-300 [&_li]:text-sm [&_li]:leading-relaxed
        [&_li_strong]:text-white
        [&_li_code]:text-green-400 [&_li_code]:bg-green-950/40 [&_li_code]:border [&_li_code]:border-green-900/40 [&_li_code]:px-1 [&_li_code]:py-0.5 [&_li_code]:rounded [&_li_code]:text-xs [&_li_code]:font-mono
        [&_a]:text-blue-400 hover:[&_a]:text-blue-300
        [&_blockquote]:border-l-2 [&_blockquote]:border-purple-500/50 [&_blockquote]:pl-3 [&_blockquote]:text-gray-400 [&_blockquote]:italic
        [&_hr]:border-gray-800 [&_hr]:my-4
      ">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {DOMPurify.sanitize(fullMarkdown)}
        </ReactMarkdown>
      </div>
    </motion.div>
  )
}
